import { required } from "ra-core";
import { useEffect } from "react";
import { useWatch, useFormContext } from "react-hook-form";
import { DateInput } from "@/components/admin/date-input";
import { FileField } from "@/components/admin/file-field";
import { FileInput } from "@/components/admin/file-input";
import { NumberInput } from "@/components/admin/number-input";
import { ReferenceInput } from "@/components/admin/reference-input";
import { SelectInput } from "@/components/admin/select-input";
import { TextInput } from "@/components/admin/text-input";
import { AutocompleteInput } from "@/components/admin/autocomplete-input";
import { Separator } from "@/components/ui/separator";
import { contactOptionText } from "../misc/ContactOption";
import { PaymentScheduleInput } from "./PaymentScheduleInput";
import { contractPaymentTypeChoices, contractStatusChoices } from "./contractModel";

export const periodChoices = [
  { id: "monthly",   name: "Ежемесячно" },
  { id: "quarterly", name: "Ежеквартально" },
  { id: "biannual",  name: "Каждые полгода" },
  { id: "annual",    name: "Ежегодно" },
];

const PERIOD_MONTHS: Record<string, number> = {
  monthly: 1, quarterly: 3, biannual: 6, annual: 12,
};

const calcNextPayment = (startDate: string, period: string): string => {
  const months = PERIOD_MONTHS[period] ?? 1;
  const today = new Date();
  const next = new Date(startDate);
  while (next <= today) {
    next.setMonth(next.getMonth() + months);
  }
  return next.toISOString().slice(0, 10);
};

export const ContractInputs = () => {
  const paymentType = useWatch({ name: "payment_type" });
  const startDate   = useWatch({ name: "start_date" });
  const period      = useWatch({ name: "period" });
  const { setValue } = useFormContext();

  const isOneTime      = paymentType === "one_time";
  const isSubscription = paymentType === "subscription";
  const isInstallment  = paymentType === "installment";

  // Автовычисление следующего платежа для подписки
  useEffect(() => {
    if (isSubscription && startDate && period) {
      setValue("next_payment_date", calcNextPayment(startDate, period), {
        shouldDirty: true,
      });
    }
  }, [isSubscription, startDate, period, setValue]);

  return (
    <div className="flex flex-col gap-6">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <TextInput source="title" validate={required()} helperText={false} />
        <ReferenceInput source="deal_id" reference="deals">
          <AutocompleteInput label="resources.contracts.fields.deal_id" helperText={false} />
        </ReferenceInput>
        <ReferenceInput source="company_id" reference="companies">
          <AutocompleteInput label="resources.contracts.fields.company_id" validate={required()} helperText={false} />
        </ReferenceInput>
        <ReferenceInput source="contact_id" reference="contacts_summary">
          <AutocompleteInput label="resources.contracts.fields.contact_id" optionText={contactOptionText} helperText={false} />
        </ReferenceInput>
      </div>

      <Separator />

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <SelectInput source="payment_type" choices={contractPaymentTypeChoices} validate={required()} helperText={false} />
        <SelectInput source="status" choices={contractStatusChoices} validate={required()} helperText={false} />

        {/* Разовый */}
        {isOneTime && <NumberInput source="total_amount" validate={required()} helperText={false} />}
        {isOneTime && <DateInput source="start_date" validate={required()} helperText={false} />}

        {/* Подписка */}
        {isSubscription && (
          <NumberInput
            source="monthly_amount"
            label="resources.contracts.fields.payment_amount"
            validate={required()}
            helperText={false}
          />
        )}
        {isSubscription && (
          <SelectInput
            source="period"
            label="resources.contracts.fields.period"
            choices={periodChoices}
            validate={required()}
            helperText={false}
          />
        )}
        {isSubscription && <DateInput source="start_date" validate={required()} helperText={false} />}
        {isSubscription && <DateInput source="end_date" helperText={false} />}

        {/* Рассрочка */}
        {isInstallment && <NumberInput source="total_amount" validate={required()} helperText={false} />}
        {isInstallment && <DateInput source="start_date" validate={required()} helperText={false} />}
        {isInstallment && <DateInput source="end_date" helperText={false} />}
      </div>

      {isInstallment && <PaymentScheduleInput source="payment_schedule" />}

      <TextInput source="notes" multiline rows={4} helperText={false} />
      <FileInput source="attachments" label="resources.contracts.fields.attachments" multiple>
        <FileField
          source="src"
          title="title"
          target="_blank"
          className="[&_a]:text-blue-600 [&_a]:hover:text-blue-800 [&_a]:underline [&_a]:break-all"
        />
      </FileInput>
    </div>
  );
};
